import { useId } from 'react';
import { format } from 'date-fns';
import _ from 'lodash';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { calculateChartData } from './calculate-chart-data';
import { CustomTooltip } from './custom-tooltip';
import type { DateRange, EventData } from '../types';

type AreaChartProps = {
  data: EventData[];
  prevData?: EventData[];
  dateRange: DateRange;
  prevDateRange?: DateRange;
  primaryColor?: string;
  secondaryColor?: string;
  formatValue?: (value: number) => string;
  // The redesigned treatment: axes, gridlines, a dashed previous period and a
  // labelled peak. Opt-in, so existing callers keep their current look.
  detailed?: boolean;
  // Off when the caller names the peak beside the chart; the dot still marks it.
  labelPeak?: boolean;
  formatAxis?: (value: number) => string;
  height?: number;
};

type ChartRow = {
  time?: number;
  name?: string;
  count?: number;
  prevTime?: number;
  prevName?: string;
  prevCount?: number;
};

const compactNumber = (value: number) =>
  new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);

const TICK = { fontSize: 12, fill: '#9ca3af' };

// Recharts passes the dot's position in `viewBox`; a plain function, since
// Recharts calls label renderers outside React's hook rules.
// YIQ brightness: dark text on a light pill, light text on a dark one.
const textOn = (color: string) => {
  const hex = /^#([0-9a-f]{6})$/i.exec(color)?.[1];
  if (!hex) return '#f9fafb';
  const value = parseInt(hex, 16);
  const brightness =
    ((value >> 16) * 299 + ((value >> 8) & 255) * 587 + (value & 255) * 114) /
    1000;
  return brightness > 128 ? '#0b1020' : '#f9fafb';
};

const peakLabel =
  (text: string, anchor: 'start' | 'middle' | 'end', color: string) =>
  ({ viewBox }: { viewBox?: { x?: number; y?: number } }) => {
    const x = viewBox?.x ?? 0;
    const y = viewBox?.y ?? 0;
    const width = text.length * 6.6 + 16;
    const left =
      anchor === 'start'
        ? x - 8
        : anchor === 'end'
          ? x - width + 8
          : x - width / 2;
    return (
      <g>
        <rect
          fill={color}
          height={22}
          rx={4}
          width={width}
          x={left}
          y={y - 36}
        />
        <text
          fill={textOn(color)}
          fontSize={12}
          fontWeight={600}
          textAnchor={anchor}
          x={x}
          y={y - 21}
        >
          {text}
        </text>
      </g>
    );
  };

const DetailedChart = ({
  rows,
  dateRange,
  primaryColor,
  secondaryColor,
  formatValue,
  formatAxis = compactNumber,
  height,
  hasPrevious,
  labelPeak,
}: {
  rows: ChartRow[];
  dateRange: DateRange;
  primaryColor: string;
  secondaryColor: string;
  formatValue?: (value: number) => string;
  formatAxis?: (value: number) => string;
  height: number;
  hasPrevious: boolean;
  labelPeak: boolean;
}) => {
  const gradientId = `fade-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const daily =
    !dateRange.interval.includes('hour') &&
    !dateRange.interval.includes('minute');
  const tickFormat = (time: number) => format(time, daily ? 'MMM d' : 'HH:mm');
  const step = Math.max(1, Math.ceil(rows.length / 5));
  const ticks = rows
    .filter((_row, index) => index % step === 0)
    .map((row) => row.time)
    .filter((time): time is number => time !== undefined);
  const peak = rows.reduce<ChartRow | undefined>(
    (top, row) => (!top || (row.count ?? 0) > (top.count ?? 0) ? row : top),
    undefined,
  );
  const showPeak = !!peak?.time && (peak.count ?? 0) > 0;
  const labelled = showPeak && labelPeak;
  const first = rows[0]?.time ?? 0;
  const span = (rows[rows.length - 1]?.time ?? 0) - first;
  const position = span > 0 ? ((peak?.time ?? 0) - first) / span : 0.5;
  // Near an edge the centered label would be clipped, so it grows inward.
  const anchor = position < 0.2 ? 'start' : position > 0.8 ? 'end' : 'middle';

  return (
    <div className="w-full" style={{ height }}>
      <ResponsiveContainer initialDimension={{ width: 100, height }}>
        <ComposedChart
          data={rows}
          // The peak pill reaches 36px above the dot; the rest keeps it clear of the chart's title.
          margin={{ top: labelled ? 48 : 12, right: 8, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor={primaryColor} stopOpacity={0.24} />
              <stop offset="1" stopColor={primaryColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            stroke="rgba(156,163,175,0.18)"
            strokeDasharray="4 4"
            vertical={false}
          />
          <XAxis
            axisLine={false}
            dataKey="time"
            domain={['dataMin', 'dataMax']}
            scale="time"
            tick={TICK}
            tickFormatter={tickFormat}
            tickLine={false}
            ticks={ticks}
            type="number"
          />
          <YAxis
            axisLine={false}
            tick={TICK}
            tickFormatter={formatAxis}
            tickLine={false}
            width={48}
          />
          {hasPrevious && (
            <Area
              dataKey="prevCount"
              fill="none"
              stroke={secondaryColor}
              strokeDasharray="4 4"
              strokeWidth={1.5}
              type="monotone"
            />
          )}
          <Area
            dataKey="count"
            fill={`url(#${gradientId})`}
            stroke={primaryColor}
            strokeWidth={2}
            type="monotone"
          />
          {showPeak && (
            <ReferenceDot
              fill={primaryColor}
              label={
                labelled
                  ? peakLabel(
                      `Peak: ${format(peak.time!, daily ? 'MMM d' : 'MMM d, HH:mm')}, ${
                        formatValue
                          ? formatValue(peak.count ?? 0)
                          : (peak.count ?? 0).toLocaleString('en-US')
                      }`,
                      anchor,
                      primaryColor,
                    )
                  : undefined
              }
              r={4}
              stroke="#ffffff"
              strokeWidth={2}
              x={peak.time}
              y={peak.count}
            />
          )}
          <Tooltip
            content={
              <CustomTooltip
                formatValue={formatValue}
                primaryColor={primaryColor}
              />
            }
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export const AreaChart = ({
  data,
  prevData,
  dateRange,
  prevDateRange,
  primaryColor = '#057BFA' /* blue-primary */,
  secondaryColor = '#9ca3af' /* gray-400 */,
  formatValue,
  detailed = false,
  labelPeak = true,
  formatAxis,
  height = 280,
}: AreaChartProps) => {
  if (!data) {
    return null;
  }

  const mappedDates = calculateChartData(data, dateRange);

  const mappedPrevDates =
    prevData && prevDateRange
      ? calculateChartData(prevData, prevDateRange)
      : [];

  if (!mappedDates.length) {
    return null;
  }

  const zipped = _.zip(mappedDates, mappedPrevDates);

  const mapZipped = zipped.map((z) => {
    return {
      time: z[0]?.time,
      name: z[0]?.name,
      count: z[0]?.count,
      prevTime: z[1]?.time,
      prevName: z[1]?.name,
      prevCount: z[1]?.count,
    };
  });

  if (detailed) {
    return (
      <DetailedChart
        dateRange={dateRange}
        formatAxis={formatAxis}
        formatValue={formatValue}
        hasPrevious={mappedPrevDates.length > 0}
        height={height}
        labelPeak={labelPeak}
        primaryColor={primaryColor}
        rows={mapZipped.map((row) => ({
          time: row.time === undefined ? undefined : Number(row.time),
          name: row.name === undefined ? undefined : String(row.name),
          count: row.count === undefined ? undefined : Number(row.count),
          prevTime:
            row.prevTime === undefined ? undefined : Number(row.prevTime),
          prevName:
            row.prevName === undefined ? undefined : String(row.prevName),
          prevCount:
            row.prevCount === undefined ? undefined : Number(row.prevCount),
        }))}
        secondaryColor={secondaryColor}
      />
    );
  }

  return (
    <div className="flex w-full flex-col">
      <div className="flex aspect-[3.5/1] w-full justify-center">
        <ResponsiveContainer initialDimension={{ width: 100, height: 100 }}>
          <ComposedChart
            data={mapZipped}
            margin={{
              right: 0,
              left: 0,
            }}
          >
            <Area
              dataKey="prevCount"
              fill={secondaryColor}
              fillOpacity={0.2}
              opacity={0.4}
              stroke={secondaryColor}
              strokeWidth={1.5}
            />
            <Area
              dataKey="count"
              fill={primaryColor}
              fillOpacity={0.2}
              stroke={primaryColor}
              strokeWidth={1.5}
            />

            <Tooltip
              content={
                <CustomTooltip
                  primaryColor={primaryColor}
                  formatValue={formatValue}
                />
              }
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-between px-2 pt-2 text-gray-400">
        <span>{format(dateRange.startDate, 'MMM dd')}</span>
        <span>{format(dateRange.endDate, 'MMM dd')}</span>
      </div>
    </div>
  );
};
